import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { CheckCircle, CloudUpload, XCircle, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import AIAvatarSubmission from "./AIAvatarSubmission";
import { showGlobalModal } from "components/GlobalModal";
import axios from "axios";

const goodPhotos = Array(4).fill("good");
const badPhotos = Array(5).fill("bad");

const Upload = ({ onSubmit }) => {
  const [files, setFiles] = useState<File[]>([]);

  const [activeTab, setActiveTab] = useState("upload");
  const [videoFile, setVideoFile] = useState(null);
  const [videoURL, setVideoURL] = useState(null);
  const [page, setPage] = useState("video");
  const [loader, setLoader] = useState(false);

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // setVideoFile(file);
    // setVideoURL(URL.createObjectURL(file)); // Local preview

    const formData = new FormData();
    formData.append("files", file);

    const controller = new AbortController();
    setLoader(true); // Start loading before upload

    try {
      const uploadResponse = await axios.post(
        "https://dev.soomuch.ai/api/v1/master/upload/tempfiles",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data", // <- Fix header here
          },
          signal: controller.signal,
        }
      );

      const videoUrl = uploadResponse?.data?.data?.[0];
      if (videoUrl) {
        setVideoURL(videoUrl); // Update with uploaded URL
        setVideoFile(file);
      }
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Upload aborted:", error.message);
      } else {
        showGlobalModal(
          error?.response?.data?.message || "Upload failed!",
          "error"
        );
      }
    } finally {
      setLoader(false); // Stop loader in both success/failure
    }
  };

  const handleDelete = () => {
    setVideoFile(null);
    setVideoURL(null);
    sessionStorage.removeItem("CreateChatbotAvatar");
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const removeImage = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/png": [],
      "image/jpeg": [],
      "image/webp": [],
    },
    maxSize: 200 * 1024 * 1024, // 200MB
  });

  const handleSubmit = () => {
    const values = { TRAINING_CONTENT_URL: videoURL };
    sessionStorage.setItem("CreateChatbotAvatar", JSON.stringify(values));
    const storedChatbot = sessionStorage.getItem("CreateChatbotAvatar");
    sessionStorage.removeItem("Chatbot");
    onSubmit();
  };

  useEffect(() => {
    const storedChatbot = sessionStorage.getItem("CreateChatbotAvatar");
    const parsedValues = storedChatbot ? JSON.parse(storedChatbot) : null;
    setVideoURL(parsedValues?.TRAINING_CONTENT_URL);
  }, []);

  return (
    <>
      {page === "video" ? (
        <>
          <div className="w-[40vw] max-w-[500px] mx-auto p-6">
            {/* Tabs */}
            <div className="w-fit flex justify-center gap-3 mb-4 bg-[#F5F5F5] border rounded-3xl mx-auto p-1">
              {["upload", "webcam"].map((tab) => (
                <motion.button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-2 py-2 rounded-full font-medium text-[2vh] transition-all duration-300 ${
                    activeTab === tab
                      ? "bg-indigo-600 text-[#fff]"
                      : "text-[#222222] "
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {tab === "upload" ? "Upload Footage" : "Record Via Webcam"}
                </motion.button>
              ))}
            </div>

            {/* Upload Section */}
            {activeTab === "upload" && !videoURL && (
              <>
                {loader ? (
                  <div className="w-full h-64 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg text-center px-6 py-4 animate-pulse">
                    <div className="text-3xl mb-2 animate-spin">🔄</div>
                    <p className="font-medium">Uploading video...</p>
                  </div>
                ) : (
                  <label
                    htmlFor="videoUpload"
                    className="w-full h-64 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer text-center px-6 py-4"
                  >
                    <input
                      id="videoUpload"
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime"
                      className="hidden"
                      onChange={handleVideoUpload}
                    />
                    <div className="text-3xl mb-2">⬆️</div>
                    <p className="font-medium">
                      Drag and drop video or click upload video
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Landscape or portrait video, mp4/mov/webm format, at least
                      30s, 2–5min recommended <br />
                      360–4k resolution, &lt;10GB
                    </p>
                    <p className="text-sm text-indigo-600 font-medium mt-2">
                      Browse video file
                    </p>
                  </label>
                )}
              </>
            )}

            {videoURL && activeTab === "upload" && (
              <div className="flex flex-col items-center">
                <video
                  src={videoURL}
                  controls
                  className="rounded-lg w-full h-auto"
                />
                <button
                  onClick={handleDelete}
                  className="mt-4 p-2 bg-red-100 hover:bg-red-200 rounded-full"
                >
                  <Trash2 className="text-red-600 w-5 h-5" />
                </button>
              </div>
            )}

            {activeTab === "webcam" && (
              <div className="text-center py-16 text-sm text-gray-500">
                Webcam recording feature coming soon...
              </div>
            )}
          </div>
        </>
      ) : page === "image" ? (
        <>
          <div>
            <div className="w-full grid grid-cols-3 ">
              <div></div>
              <div
                {...getRootProps()}
                className={`
          flex flex-col items-center justify-center w-[20vw] h-[25vh]
          border-2 border-dashed border-gray-300 rounded-lg px-3 text-center
          ${isDragActive ? "bg-blue-100" : "bg-white"}
        `}
              >
                <input {...getInputProps()} />

                <CloudUpload className="w-8 h-8" />
                <p className="text-[2vh] mt-4">
                  <span className="font-semibold bg-yellow-200 px-1 rounded">
                    Drag
                  </span>{" "}
                  and drop photos or click to upload
                </p>
                <p className="text-[2vh] text-gray-500 mt-1">
                  Upload PNG, JPG or WebP files up to 200MB
                </p>
                <a className="text-[2vh] text-indigo-600 mt-1 underline cursor-pointer">
                  Browse files
                </a>
              </div>

              <div className="flex flex-wrap gap-2">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className="relative w-[80px] h-[80px] rounded bg-gray-200 overflow-hidden"
                  >
                    <img
                      src={URL.createObjectURL(file)}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => removeImage(index)}
                      className="absolute top-0 right-0 bg-[#444] text-[#FFF] text-[2.5vh] rounded-full w-4 h-4 flex items-center justify-center"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg p-2 md:p-6 bg-[#F5F5F5] mt-4">
              <h2 className="text-[2.4vh] font-medium mb-4 border-b border-[#CCCCCC] py-2">
                Photo Requirements
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {/* Good Photos */}
                <div className="border-r border-dashed border-[#CCCCCC] pr-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="text-green-600" size={17} />
                    <span className="font-medium text-[#222] text-[2.2vh]">
                      Good Photos
                    </span>
                  </div>
                  <p className="text-[2vh] leading-5 text-gray-700 mb-4">
                    Please provide recent, high-resolution photos of yourself.
                    Include a variety of close-ups and full body shots, with
                    different angles, expressions (smiling, neutral, serious),
                    and outfits. Ensure the photos accurately reflect your
                    current appearance.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {goodPhotos.map((_, index) => (
                      <div
                        key={`good-${index}`}
                        className="w-[10vh] h-[10vh] bg-gray-200 rounded-sm"
                      />
                    ))}
                  </div>
                </div>

                {/* Bad Photos */}
                <div className="pl-4">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="text-[#E43140]" size={17} />
                    <span className="font-medium text-[#222] text-[2.2vh]">
                      Bad Photos
                    </span>
                  </div>
                  <p className="text-[2vh] leading-5 text-gray-700 mb-4">
                    No group photos, hats, sunglasses, pets, heavy filters,
                    low-resolution images, or screenshots. Avoid photos that are
                    too old, overly edited, don’t represent how you currently
                    look.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {badPhotos.map((_, index) => (
                      <div
                        key={`bad-${index}`}
                        className="w-[10vh] h-[10vh] bg-gray-200 rounded-sm"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <></>
      )}

      {activeTab === "upload" && (
        <button
          type="button"
          className="block mx-auto py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-[#fff] rounded-lg text-[2vh] font-medium"
          disabled={!videoURL}
          onClick={handleSubmit}
        >
          Submit
        </button>
      )}
    </>
  );
};

export default Upload;
